// src/pages/FruitDetail.jsx — FINAL
import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Paper, Typography, Box, Button, Chip,
  Divider, Table, TableBody, TableCell, TableContainer,
  TableRow, TextField, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, alpha,
  FormControl, InputLabel, Select, MenuItem, Autocomplete,
} from '@mui/material';
import {
  ShoppingCart, WhatsApp, LocationOn, Scale,
  LocalShipping, Inventory, Grade, ArrowBack, Info,
  Public, Star, VerifiedUser, VerifiedUser as KycIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { fruitsAPI, enquiryAPI, verificationAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Major global seaports & airports for Port of Discharge search ─────────────
const GLOBAL_PORTS = [
  'Colombo Port, Sri Lanka', 'Nhava Sheva (JNPT), Mumbai, India', 'Chennai Port, India',
  'Mundra Port, India', 'Cochin Port, India', 'Vizag Port, India', 'Kolkata Port, India',
  'Tuticorin Port, India', 'Jebel Ali Port, Dubai, UAE', 'Abu Dhabi Port, UAE',
  'Sharjah Port, UAE', 'Port of Singapore', 'Port of Shanghai, China',
  'Port of Ningbo-Zhoushan, China', 'Port of Shenzhen, China', 'Port of Guangzhou, China',
  'Port of Busan, South Korea', 'Port of Hong Kong', 'Port of Qingdao, China',
  'Port of Tianjin, China', 'Port of Kaohsiung, Taiwan', 'Port Klang, Malaysia',
  'Tanjung Pelepas, Malaysia', 'Laem Chabang, Thailand', 'Ho Chi Minh Port, Vietnam',
  'Jakarta Port, Indonesia', 'Manila Port, Philippines', 'Karachi Port, Pakistan',
  'Chittagong Port, Bangladesh', 'Muscat Port, Oman', 'Jeddah Port, Saudi Arabia',
  'Dammam Port, Saudi Arabia', 'Kuwait Port, Kuwait', 'Doha Port, Qatar',
  'Port of Rotterdam, Netherlands', 'Port of Antwerp, Belgium', 'Port of Hamburg, Germany',
  'Port of Felixstowe, UK', 'Port of Southampton, UK', 'Port of Le Havre, France',
  'Port of Barcelona, Spain', 'Port of Valencia, Spain', 'Port of Genoa, Italy',
  'Port of Piraeus, Greece', 'Port of Istanbul, Turkey', 'Port of Mombasa, Kenya',
  'Port of Durban, South Africa', 'Port of Lagos, Nigeria', 'Port of Dar es Salaam, Tanzania',
  'Port of Alexandria, Egypt', 'Port of Los Angeles, USA', 'Port of New York, USA',
  'Port of Savannah, USA', 'Port of Houston, USA', 'Port of Vancouver, Canada',
  'Port of Sydney, Australia', 'Port of Melbourne, Australia', 'Port of Santos, Brazil',
  'Port of Buenos Aires, Argentina', 'Port of Tokyo, Japan', 'Port of Osaka, Japan',
  // Airports (air freight)
  'Dubai International Airport (DXB)', 'Abu Dhabi Airport (AUH)',
  'Singapore Changi Airport (SIN)', 'Hong Kong International Airport (HKG)',
  'Incheon International Airport (ICN)', 'Heathrow Airport (LHR), UK',
  'Frankfurt Airport (FRA), Germany', 'Charles de Gaulle (CDG), France',
  'JFK International Airport (JFK), USA', 'Los Angeles International (LAX), USA',
  'Indira Gandhi International (DEL), India', 'Mumbai CSIA (BOM), India',
  'Kempegowda International (BLR), India', 'Chennai International (MAA), India',
  'Narita International (NRT), Japan', 'Guangzhou Baiyun (CAN), China',
  'Pudong International (PVG), China', 'Doha Hamad International (DOH), Qatar',
  'Riyadh King Khalid (RUH), Saudi Arabia', 'Nairobi JKIA (NBO), Kenya',
];

const _BACKEND = (process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000').replace(/\/$/, '');

// ── Image resolver ────────────────────────────────────────────────────────────
const getImageSrc = (fruit) => {
  if (!fruit) return null;
  // prefer image_path (raw filename) over image_url (may have wrong path)
  const rawPath = fruit.image_path || fruit.image_url;
  if (!rawPath) return null;
  if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) return rawPath;
  // Extract just the filename regardless of what path prefix came back
  const filename = rawPath.split('/').pop().split('\\').pop();
  if (!filename) return null;
  // Always build the correct full URL
  return `${_BACKEND}/static/uploads/fruits/${filename}`;
};

const getCategoryEmoji = (category) => {
  const map = {
    Apple: '🍎', Pineapple: '🍍', Mango: '🥭',
    Orange: '🍊', Grapes: '🍇', Banana: '🍌',
    Berries: '🫐', Melon: '🍈', Citrus: '🍋',
    Tropical: '🥥', Exotic: '🥝',
  };
  return map[category] || '🍎';
};

// ── Field helpers ─────────────────────────────────────────────────────────────

/**
 * Safely parse any value into a JS array.
 * Handles: plain array, JSON string, comma string, null/undefined.
 */
const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  try {
    const p = JSON.parse(value);
    return Array.isArray(p) ? p.filter(Boolean) : [String(p)];
  } catch {
    return String(value).split(',').map(s => s.trim()).filter(Boolean);
  }
};

/**
 * Display an array field as a comma-joined string or 'N/A'.
 * Works whether the backend sends the aliased key (varieties) or
 * the raw key (variety / origin_country etc.).
 */
const displayField = (fruit, ...keys) => {
  for (const key of keys) {
    const arr = toArray(fruit[key]);
    if (arr.length > 0) return arr.join(', ');
  }
  return 'N/A';
};

const toArrayField = (fruit, ...keys) => {
  for (const key of keys) {
    const val = fruit[key];
    if (val === null || val === undefined) continue;
    // Already an array with items
    if (Array.isArray(val) && val.length > 0) return val;
    // Non-empty string
    if (typeof val === 'string' && val.trim()) {
      // Try JSON parse first
      try {
        const p = JSON.parse(val);
        if (Array.isArray(p) && p.length > 0) return p;
      } catch {}
      // Comma-split fallback
      const parts = val.split(',').map(s => s.trim()).filter(Boolean);
      if (parts.length > 0) return parts;
    }
  }
  return [];
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(amount);
};

// ── Component ─────────────────────────────────────────────────────────────────
const FruitDetail = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [fruit,              setFruit]              = useState(null);
  const [loading,            setLoading]            = useState(true);
  const [imgError,           setImgError]           = useState(false);
  const [enquiryDialogOpen,  setEnquiryDialogOpen]  = useState(false);
  const [quantity,           setQuantity]           = useState(1);
  const [message,            setMessage]            = useState('');
  const [submitting,         setSubmitting]         = useState(false);
  const [activeStep,         setActiveStep]         = useState(0);
  // Spec selections
  const [selectedOrigin,    setSelectedOrigin]    = useState('');
  const [selectedVariety,   setSelectedVariety]   = useState('');
  const [selectedGrade,     setSelectedGrade]     = useState('');
  const [selectedSize,      setSelectedSize]      = useState('');
  const [portOfDischarge,   setPortOfDischarge]   = useState('');
  // KYC
  const [kycVerified,       setKycVerified]       = useState(null);

  useEffect(() => { fetchFruitDetail(); }, [id]); // eslint-disable-line
  useEffect(() => {
    if (user) {
      verificationAPI.getStatus()
        .then(r => setKycVerified(r.data?.user_verified === true))
        .catch(() => setKycVerified(false));
    }
  }, [user]);

  const fetchFruitDetail = async () => {
    try {
      setLoading(true);
      setImgError(false);
      const response = await fruitsAPI.getById(id);
      const raw      = response.data;
      // API returns { fruit: {...}, success: true } — unwrap the nested fruit
      const data     = raw?.fruit || raw;
      setFruit(data);
      setQuantity(parseInt(data.moq) || 1);
    } catch (error) {
      console.error('Fruit detail error:', error);
      toast.error('Failed to load fruit details');
      navigate('/fruits');
    } finally {
      setLoading(false);
    }
  };

  const handleEnquiry = () => {
    if (!user) {
      toast.error('Please login to make an enquiry');
      navigate('/login');
      return;
    }
    if (kycVerified === false) {
      toast.error('Please complete your KYC verification (GST / IEC / PAN) before submitting an enquiry.');
      navigate('/buyer/verification');
      return;
    }
    setEnquiryDialogOpen(true);
  };

  const handleSubmitEnquiry = async () => {
    try {
      setSubmitting(true);
      await enquiryAPI.create({
        fruit_id: parseInt(id),
        quantity,
        message,
        selected_origin:   selectedOrigin,
        selected_variety:  selectedVariety,
        selected_grade:    selectedGrade,
        selected_size:     selectedSize,
        port_of_discharge: portOfDischarge,
      });
      toast.success('Enquiry submitted successfully!');
      setEnquiryDialogOpen(false);
      setSelectedOrigin(''); setSelectedVariety(''); setSelectedGrade('');
      setSelectedSize(''); setPortOfDischarge(''); setMessage('');
      navigate('/buyer/enquiries');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    const text = `Hello, I'm interested in:\n\nProduct: ${fruit.name}\nVarieties: ${displayField(fruit, 'varieties', 'variety')}\nOrigin: ${displayField(fruit, 'origins', 'origin_country')}\nMOQ: ${fruit.moq}\nPrice: ${fruit.price_per_unit ? `$${fruit.price_per_unit}/unit` : 'Contact for price'}${message ? `\n\nAdditional details: ${message}` : ''}`;
    window.open(`https://wa.me/918925851195?text=${encodeURIComponent(text)}`, '_blank');
  };

  const steps   = ['Product Specifications', 'Additional Details', 'Review & Submit'];
  const moqNum  = parseInt(fruit?.moq) || 1;

  // ── Loading ──
  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={56} thickness={4} />
        <Typography sx={{ mt: 2 }} color="text.secondary">Loading fruit details...</Typography>
      </Container>
    );
  }

  if (!fruit) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">Fruit not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/fruits')} sx={{ mt: 2 }}>
          Back to Fruits
        </Button>
      </Container>
    );
  }

  // ── Resolve fields — supports both aliased and raw key names ──
  const imgSrc        = getImageSrc(fruit);
  const price         = parseFloat(fruit.price_per_unit || 0);
  const varieties     = toArrayField(fruit, 'varieties',       'variety');
  const grades        = toArrayField(fruit, 'grades',          'grade');
  const origins       = toArrayField(fruit, 'origins',         'origin_country');
  const sizes         = toArrayField(fruit, 'sizes',           'size');
  const containerTypes= toArrayField(fruit, 'container_types', 'containerTypes', 'container_type');
  const ports         = toArrayField(fruit, 'ports',           'port_of_discharge');

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>

      <Button startIcon={<ArrowBack />} onClick={() => navigate('/fruits')} sx={{ mb: 3 }}>
        Back to Fruits
      </Button>

      <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }} elevation={2}>
        <Grid container spacing={4}>

          {/* ══ LEFT ══ */}
          <Grid item xs={12} md={5}>

            {/* Image */}
            <Box sx={{
              width: '100%', height: 380, borderRadius: 3, overflow: 'hidden',
              bgcolor: 'grey.100', position: 'relative',
              border: '1px solid', borderColor: 'divider',
            }}>
              {imgSrc && !imgError ? (
                <Box
                  component="img"
                  src={imgSrc}
                  alt={fruit.name}
                  onError={() => setImgError(true)}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box sx={{
                  width: '100%', height: '100%',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  background: 'linear-gradient(135deg,#e8f5e9,#c8e6c9)',
                }}>
                  <Typography sx={{ fontSize: '6rem', lineHeight: 1 }}>
                    {getCategoryEmoji(fruit.category)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {fruit.name}
                  </Typography>
                </Box>
              )}
              {fruit.active && (
                <Chip
                  label="Available"
                  icon={<VerifiedUser sx={{ fontSize: '14px !important' }} />}
                  size="small"
                  sx={{
                    position: 'absolute', top: 12, right: 12,
                    bgcolor: alpha('#4caf50', 0.9), color: 'white',
                    '& .MuiChip-icon': { color: 'white' },
                  }}
                />
              )}
            </Box>

            {/* Category + origins */}
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {fruit.category && (
                <Chip label={fruit.category} color="primary" variant="outlined" />
              )}
              {origins.map((o, i) => (
                <Chip key={i} label={o} size="small"
                  icon={<Public sx={{ fontSize: '14px !important' }} />}
                  sx={{ bgcolor: alpha('#1976d2', 0.08) }} />
              ))}
            </Box>

            {/* CTA */}
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button variant="contained" fullWidth size="large"
                startIcon={<ShoppingCart />} onClick={handleEnquiry}
                sx={{
                  background: 'linear-gradient(90deg,#2e7d32,#4caf50)',
                  py: 1.5, borderRadius: 2, fontWeight: 'bold', fontSize: '1rem',
                }}>
                {user ? 'Start Enquiry' : 'Login to Enquire'}
              </Button>
              <Button variant="outlined" fullWidth size="large"
                startIcon={<WhatsApp sx={{ color: '#25d366' }} />}
                onClick={handleWhatsApp}
                sx={{ borderColor: '#25d366', color: '#25d366', py: 1.5, borderRadius: 2 }}>
                Contact on WhatsApp
              </Button>
            </Box>

            {/* Price card */}
            {price > 0 && (
              <Paper sx={{
                mt: 3, p: 2.5, borderRadius: 2, textAlign: 'center',
                background: 'linear-gradient(135deg,#e8f5e9,#f1f8e9)',
                border: '1px solid #c8e6c9',
              }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Price Per Unit
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {formatCurrency(price)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  MOQ: {fruit.moq}
                </Typography>
              </Paper>
            )}
          </Grid>

          {/* ══ RIGHT ══ */}
          <Grid item xs={12} md={7}>

            {/* Title + price */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {fruit.name}
              </Typography>
              {price > 0 ? (
                <Typography variant="h5" color="primary.main" fontWeight="bold" sx={{ mb: 1 }}>
                  {formatCurrency(price)}
                  <Typography component="span" variant="body1" color="text.secondary"> / unit</Typography>
                </Typography>
              ) : (
                <Chip label="Price on request" color="warning" variant="outlined" sx={{ mb: 1 }} />
              )}
              {fruit.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2, lineHeight: 1.8 }}>
                  {fruit.description}
                </Typography>
              )}
            </Box>

            {/* Varieties */}
            {varieties.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Available Varieties
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {varieties.map((v, i) => (
                    <Chip key={i} label={v} size="small"
                      sx={{ bgcolor: alpha('#4caf50', 0.1), color: '#2e7d32', fontWeight: 500 }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Grades */}
            {grades.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Quality Grades
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {grades.map((g, i) => (
                    <Chip key={i} label={g} size="small"
                      icon={<Star sx={{ fontSize: '14px !important', color: '#ff9800 !important' }} />}
                      sx={{ bgcolor: alpha('#ff9800', 0.1) }} />
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Specs table */}
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" /> Product Specifications
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
              <Table>
                <TableBody>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', width: '42%', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOn fontSize="small" color="primary" /> Origin Country
                      </Box>
                    </TableCell>
                    <TableCell>
                      {origins.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {origins.map((o, i) => (
                            <Chip key={i} label={o} size="small" variant="outlined" />
                          ))}
                        </Box>
                      ) : 'N/A'}
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      Variety
                    </TableCell>
                    <TableCell>
                      {varieties.length > 0 ? varieties.join(', ') : 'N/A'}
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Grade fontSize="small" color="primary" /> Grade
                      </Box>
                    </TableCell>
                    <TableCell>
                      {grades.length > 0 ? grades.join(', ') : 'N/A'}
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Scale fontSize="small" color="primary" /> Size
                      </Box>
                    </TableCell>
                    <TableCell>
                      {sizes.length > 0 ? sizes.join(', ') : 'N/A'}
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Inventory fontSize="small" color="primary" /> MOQ
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={fruit.moq || '1 container'} color="primary" size="small" />
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocalShipping fontSize="small" color="primary" /> Container Type
                      </Box>
                    </TableCell>
                    <TableCell>
                      {containerTypes.length > 0 ? containerTypes.join(', ') : 'N/A'}
                    </TableCell>
                  </TableRow>

                  <TableRow hover>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha('#f5f5f5', 0.5) }}>
                      Port of Discharge
                    </TableCell>
                    <TableCell>
                      {ports.length > 0 ? ports.join(', ') : 'N/A'}
                    </TableCell>
                  </TableRow>

                </TableBody>
              </Table>
            </TableContainer>

            <Alert severity="info" icon={<Info />} sx={{ borderRadius: 2 }}>
              <Typography variant="body2">
                • All prices are per unit in US dollars<br />
                • Minimum order quantity applies<br />
                • Shipping costs calculated separately<br />
                • Contact us for bulk orders and custom requirements
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </Paper>

      {/* ══ Enquiry Dialog ══ */}
      <Dialog
        open={enquiryDialogOpen}
        onClose={() => { setEnquiryDialogOpen(false); setActiveStep(0); }}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg,#1b5e20,#2e7d32)',
          color: 'white', fontWeight: 'bold',
        }}>
          Make Enquiry — {fruit.name}
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 1 }}>
            {steps.map((label) => (
              <Step key={label}><StepLabel>{label}</StepLabel></Step>
            ))}
          </Stepper>

          {/* Step 0 — Product Specifications */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>Select Your Product Specifications</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose the exact origin, variety, grade and size for your order.
              </Typography>

              <Grid container spacing={2}>

                {/* Origin Country */}
                {origins.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Origin Country</InputLabel>
                      <Select
                        value={selectedOrigin}
                        label="Origin Country"
                        onChange={(e) => setSelectedOrigin(e.target.value)}
                      >
                        {origins.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Variety */}
                {varieties.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Variety</InputLabel>
                      <Select
                        value={selectedVariety}
                        label="Variety"
                        onChange={(e) => setSelectedVariety(e.target.value)}
                      >
                        {varieties.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Grade */}
                {grades.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Grade</InputLabel>
                      <Select
                        value={selectedGrade}
                        label="Grade"
                        onChange={(e) => setSelectedGrade(e.target.value)}
                      >
                        {grades.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Size */}
                {sizes.length > 0 && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Size</InputLabel>
                      <Select
                        value={selectedSize}
                        label="Size"
                        onChange={(e) => setSelectedSize(e.target.value)}
                      >
                        {sizes.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Quantity */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth type="number"
                    label={`Quantity (containers, MOQ: ${fruit.moq})`}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(moqNum, parseInt(e.target.value) || moqNum))}
                    inputProps={{ min: moqNum }}
                  />
                </Grid>

                {/* Port of Discharge */}
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    freeSolo
                    options={GLOBAL_PORTS}
                    value={portOfDischarge}
                    onInputChange={(_, val) => setPortOfDischarge(val)}
                    renderInput={(params) => (
                      <TextField {...params} label="Port of Discharge" placeholder="Search seaport or airport..." />
                    )}
                  />
                </Grid>
              </Grid>

              {price > 0 && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    Estimated total: <strong>{formatCurrency(quantity * price)}</strong>
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    Advance (30%): {formatCurrency(quantity * price * 0.3)}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Step 1 — Message */}
          {activeStep === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>Additional Details</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please provide any special requirements or additional information
              </Typography>
              <TextField
                fullWidth multiline rows={6}
                label="Message / Requirements"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g. Need delivery by [date], special packaging, quality specs..."
              />
            </Box>
          )}

          {/* Step 2 — Review */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>Review Enquiry</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Product</TableCell>
                      <TableCell>{fruit.name}</TableCell>
                    </TableRow>
                    {selectedOrigin && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Origin Country</TableCell>
                        <TableCell>{selectedOrigin}</TableCell>
                      </TableRow>
                    )}
                    {selectedVariety && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Variety</TableCell>
                        <TableCell>{selectedVariety}</TableCell>
                      </TableRow>
                    )}
                    {selectedGrade && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Grade</TableCell>
                        <TableCell>{selectedGrade}</TableCell>
                      </TableRow>
                    )}
                    {selectedSize && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Size</TableCell>
                        <TableCell>{selectedSize}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Quantity (MOQ: {fruit.moq})</TableCell>
                      <TableCell>{quantity} containers</TableCell>
                    </TableRow>
                    {portOfDischarge && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Port of Discharge</TableCell>
                        <TableCell>{portOfDischarge}</TableCell>
                      </TableRow>
                    )}
                    {price > 0 && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Estimated Total</TableCell>
                        <TableCell>
                          <Typography color="primary.main" fontWeight="bold">
                            {formatCurrency(quantity * price)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {message && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Requirements</TableCell>
                        <TableCell>{message}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="success">
                After submission, our team will contact you within 24 hours.
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={() => { setEnquiryDialogOpen(false); setActiveStep(0); }}>
            Cancel
          </Button>
          {activeStep > 0 && (
            <Button variant="outlined" onClick={() => setActiveStep(p => p - 1)}>
              Back
            </Button>
          )}
          {activeStep < steps.length - 1 ? (
            <Button variant="contained" onClick={() => setActiveStep(p => p + 1)}
              sx={{ background: 'linear-gradient(90deg,#2e7d32,#4caf50)' }}>
              Next
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmitEnquiry} disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <ShoppingCart />}
              sx={{ background: 'linear-gradient(90deg,#2e7d32,#4caf50)' }}>
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FruitDetail;